'use client';

/**
 * Data + filter state hook for the quiz catalog page.
 *
 * Module: app/quizzes/use-quizzes
 * Layer:  hook
 *
 * Purpose:
 *     Encapsulates quiz-catalog fetching and client-side filtering (course id
 *     + title search) for the listing page so the page component stays small
 *     and every function clears the CC<=5 / <=60-line gates. The catalog is
 *     fetched once; the course filter and search term are local UI state that
 *     narrow the in-memory list (the catalog is small enough that re-fetching
 *     per keystroke would be wasteful).
 *
 * AI context:
 *     - Entry points called by: src/app/quizzes/page.tsx (QuizzesContent).
 *     - Depends on: lib/quizzes (fetchQuizzes + types).
 *     - Side effects: network I/O on mount (aborted on unmount).
 *     - Invariants: a fetch failure flips `errored` and never throws to React;
 *       the in-flight request is aborted in cleanup.
 *     - Replacement cost: LOW.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { type QuizSummary, fetchQuizzes } from '@/lib/quizzes';

/** A course option derived from the loaded quizzes (id + title). */
export interface CourseOption {
  readonly id: number;
  readonly title: string;
}

/** Everything the catalog page needs to render and react to user input. */
export interface QuizzesController {
  readonly quizzes: readonly QuizSummary[];
  readonly courseOptions: readonly CourseOption[];
  readonly loading: boolean;
  readonly errored: boolean;
  readonly draftQuery: string;
  readonly activeQuery: string;
  readonly courseId: number | null;
  readonly setDraftQuery: (next: string) => void;
  readonly submitSearch: () => void;
  readonly selectCourse: (id: number | null) => void;
  readonly clearFilters: () => void;
  readonly retry: () => void;
}

/** The loaded catalog plus its in-flight / error flags. */
interface CatalogState {
  readonly all: readonly QuizSummary[];
  readonly loading: boolean;
  readonly errored: boolean;
}

/** Resolve the next catalog state from a fetch result (pure). */
function nextCatalogState(fresh: readonly QuizSummary[] | null): CatalogState {
  if (fresh === null) {
    return { all: [], loading: false, errored: true };
  }
  return { all: fresh, loading: false, errored: false };
}

/** Load the full quiz catalog once, aborting on unmount. */
function useCatalog(reloadKey: number): CatalogState {
  const [state, setState] = useState<CatalogState>({ all: [], loading: true, errored: false });
  useEffect(() => {
    const controller = new AbortController();
    const run = async (): Promise<void> => {
      const fresh = await fetchQuizzes(undefined, controller.signal);
      if (!controller.signal.aborted) {
        setState(nextCatalogState(fresh));
      }
    };
    void run();
    return (): void => controller.abort();
  }, [reloadKey]);
  return state;
}

/** Build the de-duplicated, title-sorted course filter options. */
function deriveCourseOptions(quizzes: readonly QuizSummary[]): readonly CourseOption[] {
  const byId = new Map<number, string>();
  for (const quiz of quizzes) {
    if (quiz.course_id !== null && quiz.course_title !== null) {
      byId.set(quiz.course_id, quiz.course_title);
    }
  }
  return [...byId.entries()]
    .map(([id, title]) => ({ id, title }))
    .toSorted((a, b) => a.title.localeCompare(b.title));
}

/** Apply the active course + search filters to the catalog (pure). */
function applyFilters(
  quizzes: readonly QuizSummary[],
  courseId: number | null,
  query: string,
): readonly QuizSummary[] {
  const needle = query.trim().toLowerCase();
  return quizzes.filter((quiz) => {
    const courseOk = courseId === null || quiz.course_id === courseId;
    const queryOk = needle === '' || quiz.title.toLowerCase().includes(needle);
    return courseOk && queryOk;
  });
}

/**
 * Catalog data + filter controller for the quiz listing page.
 *
 * @returns A {@link QuizzesController} exposing filtered quizzes and actions.
 */
export function useQuizzes(): QuizzesController {
  const [reloadKey, setReloadKey] = useState(0);
  const catalog = useCatalog(reloadKey);
  const [draftQuery, setDraftQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [courseId, setCourseId] = useState<number | null>(null);

  const courseOptions = useMemo(() => deriveCourseOptions(catalog.all), [catalog.all]);
  const quizzes = useMemo(
    () => applyFilters(catalog.all, courseId, activeQuery),
    [catalog.all, courseId, activeQuery],
  );

  const submitSearch = useCallback((): void => setActiveQuery(draftQuery), [draftQuery]);
  const selectCourse = useCallback((id: number | null): void => setCourseId(id), []);
  const clearFilters = useCallback((): void => {
    setDraftQuery('');
    setActiveQuery('');
    setCourseId(null);
  }, []);
  const retry = useCallback((): void => setReloadKey((key) => key + 1), []);

  return {
    quizzes,
    courseOptions,
    loading: catalog.loading,
    errored: catalog.errored,
    draftQuery,
    activeQuery,
    courseId,
    setDraftQuery,
    submitSearch,
    selectCourse,
    clearFilters,
    retry,
  };
}
